/**
 * Address Value Transformer
 *
 * Transforms address field values according to a mapping configuration.
 * Maps values like "Street" to "street_type" based on configured mappings.
 *
 * @param {Object} data - Address data object with field values
 * @param {Object} addressValueMap - Mapping configuration: { fieldName: ['oldValue=newValue', ...] }
 * @param {boolean} [caseInsensitive=false] - Use case-insensitive matching with regex
 * @returns {Object} Transformed address data
 *
 * @example
 * const data = { type: 'Street' };
 * const map = { type: ['Street=street_type', 'Avenue=avenue_type'] };
 * transformValues(data, map); // => { type: 'street_type' }
 *
 * @example Case-insensitive matching
 * const data = { type: 'street' };
 * const map = { type: ['Street=street_type'] };
 * transformValues(data, map, true); // => { type: 'street_type' }
 */
export function transformValues(
  data,
  addressValueMap,
  caseInsensitive = false
) {
  if (!addressValueMap || !data) {
    return data;
  }
  const fieldsToMap = Object.keys(addressValueMap).filter(
    fieldName => addressValueMap[fieldName] !== null
  );

  fieldsToMap.forEach(fieldName => {
    const prevValue = data[fieldName];
    const mappedValue = addressValueMap[fieldName].find(value =>
      caseInsensitive
        ? new RegExp(prevValue, 'i').test(value)
        : value.includes(prevValue)
    );

    data[fieldName] = mappedValue ? mappedValue.split('=')[1] : undefined;
  });

  return data;
}
